import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User, Home, MessageCircle, Calendar, HelpCircle, Users, Settings, ArrowLeft, UserPlus, LogIn, FileText, DollarSign, Mail, UserCog, BookOpen } from 'lucide-react';
import Icon from './Icon';
import Logo from './Logo';
import { trackEvent } from '../utils/analytics';

const MobileMenu = ({ user, onLogout, isLandingPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.mobile-menu-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const scrollToMainLandingSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    onLogout();
    setIsOpen(false);
  };

  const handleHomeClick = () => {
    const nextAppUrl = process.env.REACT_APP_NEXT_URL || 'https://yourtherapist.se';
    window.location.href = nextAppUrl;
  };

  const getLandingPageMenuItems = () => {
    // Check if we're on the therapist landing page
    if (location.pathname === '/therapist-landing') {
      return [
        {
          icon: <Home className="w-5 h-5" />,
          label: 'Hem',
          onClick: handleHomeClick
        },
        {
          icon: <HelpCircle className="w-5 h-5" />,
          label: 'Fördelar',
          onClick: () => scrollToSection('benefits')
        },
        {
          icon: <FileText className="w-5 h-5" />,
          label: 'Så fungerar det',
          onClick: () => scrollToSection('how-it-works')
        },
        {
          icon: <DollarSign className="w-5 h-5" />,
          label: 'Avgifter',
          onClick: () => scrollToSection('pricing')
        },
        {
          icon: <BookOpen className="w-5 h-5" />,
          label: 'Blog',
          onClick: () => handleNavigation('/blog')
        },
        {
          icon: <Mail className="w-5 h-5" />,
          label: 'Kontakt',
          onClick: () => handleNavigation('/contact')
        },
        {
          icon: <LogIn className="w-5 h-5" />,
          label: 'Logga in',
          onClick: () => handleNavigation('/login')
        },
        {
          icon: <UserPlus className="w-5 h-5" />,
          label: 'Anslut dig',
          onClick: () => handleNavigation('/therapist/signup'),
          highlight: true
        }
      ];
    }
    
    // Default client landing page menu items
    return [
      {
        icon: <Home className="w-5 h-5" />,
        label: 'Hem',
        onClick: handleHomeClick
      },
      {
        icon: <HelpCircle className="w-5 h-5" />,
        label: 'Utforska livsteman',
        onClick: () => {
          trackEvent('Discover Life Themes Button Click', { location: 'mobile-menu' });
          handleNavigation('/livsteman');
        }
      },
      {
        icon: <Users className="w-5 h-5" />,
        label: 'Psykoterapi',
        onClick: () => {
          trackEvent('Therapists Link Click', { location: 'mobile-menu' });
          handleNavigation('/therapists');
        }
      },
      {
        icon: <BookOpen className="w-5 h-5" />,
        label: 'Blog',
        onClick: () => handleNavigation('/blog')
      },
      {
        icon: <Mail className="w-5 h-5" />,
        label: 'Kontakt',
        onClick: () => handleNavigation('/contact')
      },
      {
        icon: <Users className="w-5 h-5" />,
        label: 'För terapeuter',
        onClick: () => handleNavigation('/therapist-landing')
      },
      {
        icon: <LogIn className="w-5 h-5" />,
        label: 'Logga in',
        onClick: () => handleNavigation('/login')
      },
      {
        icon: <MessageCircle className="w-5 h-5" />,
        label: 'Kom igång',
        onClick: () => {
          trackEvent('Start Chat Button Click', { location: 'mobile-menu' });
          handleNavigation('/instant-chat');
        },
        highlight: true
      }
    ];
  };

  const getAppMenuItems = () => {
    const baseItems = [
      {
        icon: <HelpCircle className="w-5 h-5" />,
        label: 'Hjälp',
        onClick: () => handleNavigation('/help')
      }
    ];

    if (!user) {
      // For public pages like Help, Contact, etc. when user is not logged in
      return [
        {
          icon: <Home className="w-5 h-5" />,
          label: 'Tillbaka hem',
          onClick: handleHomeClick
        },
        {
          icon: <BookOpen className="w-5 h-5" />,
          label: 'Blog',
          onClick: () => handleNavigation('/blog')
        },
        ...baseItems
      ];
    }

    if (user.role === 'patient') {
      return [
        {
          icon: <Home className="w-5 h-5" />,
          label: 'Hem',
          onClick: () => handleNavigation('/client')
        },
        {
          icon: <MessageCircle className="w-5 h-5" />,
          label: 'AI Chat',
          onClick: () => handleNavigation('/client/chat')
        },
        {
          icon: <FileText className="w-5 h-5" />,
          label: 'Sessioner',
          onClick: () => handleNavigation('/client/sessions')
        },
        {
          icon: <Users className="w-5 h-5" />,
          label: 'Min Terapeut',
          onClick: () => handleNavigation('/client/therapist')
        },
        ...baseItems,
        {
          icon: <UserCog className="w-5 h-5" />,
          label: 'Min profil',
          onClick: () => handleNavigation('/profile')
        },
        {
          icon: <LogOut className="w-5 h-5" />,
          label: 'Logga ut',
          onClick: handleLogout,
          danger: true
        }
      ];
    }

    if (user.role === 'therapist') {
      return [
        {
          icon: <Home className="w-5 h-5" />,
          label: 'Hem',
          onClick: () => handleNavigation('/therapist')
        },
        {
          icon: <Users className="w-5 h-5" />,
          label: 'Klienter',
          onClick: () => handleNavigation('/therapist')
        },
        {
          icon: <Mail className="w-5 h-5" />,
          label: 'Inbjudningar',
          onClick: () => handleNavigation('/therapist/invitations')
        },
        ...baseItems,
        {
          icon: <UserCog className="w-5 h-5" />,
          label: 'Min profil',
          onClick: () => handleNavigation('/profile')
        },
        {
          icon: <LogOut className="w-5 h-5" />,
          label: 'Logga ut',
          onClick: handleLogout,
          danger: true
        }
      ];
    }

    return baseItems;
  };

  const menuItems = isLandingPage ? getLandingPageMenuItems() : getAppMenuItems();

  return (
    <div className="mobile-menu-container block lg:hidden">
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative z-[60] p-2 rounded-md transition-colors ${
          isLandingPage 
            ? 'text-gray-700 hover:bg-gray-100' 
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Toggle menu"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Menu className="w-6 h-6" />
        )}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity duration-300 z-[58]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-out Menu */}
      <div 
        className={`
          fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-[59]
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <Logo 
            size="large"
            textColor="text-gray-900"
            hoverTextColor="hover:text-pink-600"
          />
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info (for app menu) */}
        {!isLandingPage && user && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                user.role === 'therapist' ? 'bg-red-500' : 'bg-emerald-500'
              }`}>
                {user.role === 'therapist' ? (
                  <Icon name="message-circle-heart" size={20} />
                ) : (
                  <Icon name="user" size={20} />
                )}
              </div>
              <div>
                <div className="font-semibold text-gray-900">{user.name}</div>
                <div className="text-sm text-gray-500 capitalize">
                  {user.role === 'therapist' ? 'Terapeut' : 'Klient'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-4">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors
                  ${item.highlight 
                    ? 'bg-brand-primary text-white hover:bg-brand-primary-dark' 
                    : item.danger
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p>Your Therapist</p>
            <p className="text-xs mt-1">Mental hälsovård med AI</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
